import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import logo from '../assets/logo.jpg';
import './Login.css';

const Login: React.FC = () => {
  const { login, verify2FA, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  const [phase, setPhase] = useState<'login' | '2fa'>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [tempToken, setTempToken] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Redirect if already authenticated
  React.useEffect(() => {
    if (isAuthenticated) {
      navigate('/', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await login(email, password);
      if (response.mustResetPassword) {
        navigate(`/reset-password?token=${response.resetToken}`);
        return;
      }
      if (response.requires2FA) {
        setTempToken(response.tempToken);
        setPhase('2fa');
      }
    } catch (err: any) {
      const message = err.response?.data?.message || 'Error al iniciar sesión';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const handleVerify2FA = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await verify2FA(tempToken, code);
      navigate('/', { replace: true });
    } catch (err: any) {
      const message = err.response?.data?.message || 'Código inválido';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login-wrapper">
        <div className="brand-panel">
          <img src={logo} alt="Club Participa" className="brand-logo" />
          <div className="brand-tagline">
            <h1>Gestión de inversiones</h1>
            <p>Plataforma integral para el seguimiento de tus productos de inversión</p>
          </div>
        </div>

        <div className="form-panel">
          <div className="form-inner">
            {phase === 'login' ? (
              <form onSubmit={handleLogin}>
                <h2 className="form-title">Bienvenido</h2>
                <p className="form-subtitle">Accede con tu cuenta para continuar</p>

                <div className="form-group">
                  <label htmlFor="email">Email</label>
                  <input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="tu@email.com"
                    required
                    autoFocus
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="password">Contraseña</label>
                  <input
                    id="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Tu contraseña"
                    required
                  />
                </div>

                {error && <div className="login-error">{error}</div>}

                <button type="submit" className="login-button" disabled={loading}>
                  {loading ? 'Iniciando sesión...' : 'Iniciar sesión'}
                </button>

                <Link to="/forgot-password" className="forgot-password-link">
                  ¿Olvidaste tu contraseña?
                </Link>
              </form>
            ) : (
              <form onSubmit={handleVerify2FA}>
                <h2 className="form-title">Verificación en dos pasos</h2>
                <p className="two-fa-message">Se ha enviado un código de verificación a tu email</p>

                <div className="form-group">
                  <label htmlFor="code">Código de verificación</label>
                  <input
                    id="code"
                    type="text"
                    value={code}
                    onChange={(e) => setCode(e.target.value.toUpperCase())}
                    placeholder="XXXXXX"
                    maxLength={6}
                    required
                    autoFocus
                    className="code-input"
                  />
                </div>

                {error && <div className="login-error">{error}</div>}

                <button type="submit" className="login-button" disabled={loading}>
                  {loading ? 'Verificando...' : 'Verificar'}
                </button>

                <button
                  type="button"
                  className="back-button"
                  onClick={() => { setPhase('login'); setError(''); setCode(''); }}
                >
                  Volver al inicio de sesión
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
